import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Paper, Tooltip } from '@mui/material';
import { DataGrid, GridActionsColDef, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import { MijnOrganisatieListItem } from '../../../models/user/mijnOrganisatieListItem';

import ProgressBar from '../../controls/ProgressBar';
import ErrorMessage from '../../controls/ErrorMessage';
import MijnOrganisatiesActionMenu from './MijnOrganisatiesActionMenu';

import { userManagementService } from '../../../services/user-management.service';
import { authService } from '../../../services/auth.service';
import { getRoleLabel } from '../../../models/user/userRole';


const MijnOrganisaties: React.FC = () => {
  const navigate = useNavigate();
  const [isAdmin, setAdmin] = useState<boolean>(false);
  const [mijnOrganisaties, setMijnOrganisaties] = useState<MijnOrganisatieListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasFetchedData, setHasFetchedData] = useState(false);

  

  const gridColumns = useMemo<(GridColDef<MijnOrganisatieListItem> | GridActionsColDef<MijnOrganisatieListItem>)[]>(() => {
    const columns: (GridColDef<MijnOrganisatieListItem> | GridActionsColDef<MijnOrganisatieListItem>)[] = [
      {
        field: "icon",
        headerName: "Actief",
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<MijnOrganisatieListItem>) => {
          const organisatie = userManagementService.getStoredOrganisatie();
          if (!organisatie) return null; // Geen icoon tonen
          
          const isMatch = params.row.id && organisatie.id === params.row.id.toString(); // Vergelijken met id van de rij
          return isMatch ? (
            <div className="status-icon">
              <Tooltip title="Deze organisatie is op dit moment actief" placement="right" arrow>
                <Icon color="success" style={{ fontSize: 24 }}>check_circle</Icon>
              </Tooltip>
            </div>
          ) : null; // Geen icoon tonen
        },
      },
      { field: "id", headerName: "Id", flex: 2 },
      { field: "displayName", headerName: "Naam", flex: 3 },
      {
        field: "userType",
        headerName: "Type",
        flex: 2,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<MijnOrganisatieListItem>) => (
          <span>{getRoleLabel(params.row.userType)}</span>
        ),
      },
      {
        field: "Acties",
        headerName: "Acties",
        renderHeader: () => <></>,
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<MijnOrganisatieListItem>) => (
          <MijnOrganisatiesActionMenu params={params} onOrganisatieSimulation={fetchMijnOrganisaties} />
        ),
      },
    ];
  
    // Remove 'id' column if user is not admin
    return !isAdmin ? columns.filter((col) => col.field !== "id") : columns;
  }, [hasFetchedData, isAdmin]); // Add isAdmin as a dependency

  const paginationModel = { page: 0, pageSize: 10 };

  useEffect(() => {
    setIsAdmin();
    // Ensure the fetch happens only once
    if (!hasFetchedData) {
      fetchMijnOrganisaties();
      
      setHasFetchedData(true); // Mark as fetched
    }
  }, []); // Empty dependency array ensures this runs once when the component mounts

  const setIsAdmin = async () => {
    const isAdmin = await authService.isAdmin();
    setAdmin(isAdmin);
  }

  const fetchMijnOrganisaties = async () => {
    setLoading(true);
    try {
      const items = await userManagementService.mijnOrganisaties();
      setMijnOrganisaties(items); // Assuming the API response is a JSON object containing array of items
      setLoading(false);
    }
    catch (error: any) {
      // If token refresh failed, redirect to the logout page
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError(error.message || 'Er is een fout opgetreden');
      setLoading(false);
    }
  };

  return (
    <div>
      {/* <p className="center">
        Paragraph content
      </p> */}

      <div className="grid-responsive">
        <Paper sx={{ width: '100%' }}>
          <DataGrid
            rows={mijnOrganisaties}
            columns={gridColumns}
            pageSizeOptions={[5, 10, 25, 50, 100]}
            initialState={{ pagination: { paginationModel } }}
            disableRowSelectionOnClick
            // localeText={{
            //   MuiTablePagination: {
            //     labelRowsPerPage: 'Aantal per pagina:',
            //   },
            // }}
            // style={{
            //   height: gridHeight,
            //   border: '1px solid #ccc', // Optional border
            // }}
            // checkboxSelection
            sx={{ border: 0 }}
          />
        </Paper>
      </div>

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}

    </div>
  );
};

export default MijnOrganisaties;
